import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClienteService } from "@/services/StorageService";
import { useAuth } from "@/contexts/AuthContext";
import CepInput from "@/components/address/CepInput";
import { Separator } from "@/components/ui/separator";
import { z } from "zod";

// Schema for solar client form
const clienteSolarSchema = z.object({
  nome: z.string().min(3, "Nome deve ter no mínimo 3 caracteres"),
  documento: z.string().min(11, "CPF/CNPJ inválido"),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  celular: z.string().min(10, "Celular inválido").optional().or(z.literal("")),
  status: z.string().default("Ativo"),
  cep: z.string().min(8, "CEP inválido").optional().or(z.literal("")),
  logradouro: z.string().optional().or(z.literal("")),
  bairro: z.string().optional().or(z.literal("")),
  cidade: z.string().optional().or(z.literal("")),
  estado: z.string().length(2, "Estado deve ter 2 caracteres").optional().or(z.literal("")),
  tipoInstalacao: z.enum(["residencial", "comercial", "industrial", "rural"]).default("residencial"),
  potenciaInstalada: z.number().min(0).optional(),
  areaOcupada: z.number().min(0).optional(),
  fabricantePaineis: z.string().optional().or(z.literal("")),
  modeloPaineis: z.string().optional().or(z.literal("")),
  quantidadePaineis: z.number().min(0).optional(),
  fabricanteInversor: z.string().optional().or(z.literal("")),
  modeloInversor: z.string().optional().or(z.literal("")),
  quantidadeInversores: z.number().min(0).optional(),
  dataInstalacao: z.string().optional().or(z.literal(""))
});

type ClienteSolarFormValues = z.infer<typeof clienteSolarSchema>;

interface ClienteSolarFormProps {
  onSalvar?: (dados: ClienteSolarFormValues) => void;
  isEmbedded?: boolean;
}

const ClienteSolarForm = ({ onSalvar, isEmbedded = false }: ClienteSolarFormProps) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  
  // Carregar painéis e inversores cadastrados
  const [paineisCadastrados, setPaineisCadastrados] = useState<any[]>([]);
  const [inversoresCadastrados, setInversoresCadastrados] = useState<any[]>([]);
  
  useEffect(() => {
    try {
      const paineisSalvos = localStorage.getItem('paineisSolares');
      const inversoresSalvos = localStorage.getItem('inversoresSolares');
      
      if (paineisSalvos) {
        setPaineisCadastrados(JSON.parse(paineisSalvos));
      }
      
      if (inversoresSalvos) {
        setInversoresCadastrados(JSON.parse(inversoresSalvos));
      }
    } catch (error) {
      console.error("Erro ao carregar equipamentos cadastrados:", error);
    }
  }, []);

  const form = useForm<ClienteSolarFormValues>({
    resolver: zodResolver(clienteSolarSchema),
    defaultValues: {
      nome: "",
      documento: "",
      email: "",
      celular: "",
      status: "Ativo",
      cep: "",
      logradouro: "",
      bairro: "",
      cidade: "",
      estado: "",
      tipoInstalacao: "residencial",
      potenciaInstalada: 0,
      areaOcupada: 0,
      fabricantePaineis: "",
      modeloPaineis: "",
      quantidadePaineis: 0,
      fabricanteInversor: "",
      modeloInversor: "",
      quantidadeInversores: 0,
      dataInstalacao: ""
    }
  });

  const handleCepFound = (address: any) => {
    form.setValue("cep", address.cep);
    form.setValue("logradouro", address.logradouro);
    form.setValue("bairro", address.bairro);
    form.setValue("cidade", address.localidade);
    form.setValue("estado", address.uf);
  };

  const onPainelSelect = (id: string) => {
    const painel = paineisCadastrados.find(p => p.id === id);
    if (painel) {
      form.setValue("fabricantePaineis", painel.marca);
      form.setValue("modeloPaineis", painel.modelo);
    }
  };
  
  const onInversorSelect = (id: string) => {
    const inversor = inversoresCadastrados.find(i => i.id === id);
    if (inversor) {
      form.setValue("fabricanteInversor", inversor.marca);
      form.setValue("modeloInversor", inversor.modelo);
    }
  };

  const onSubmit = async (values: ClienteSolarFormValues) => {
    setIsSubmitting(true);
    try {
      // If embedded in calculator, just return the values
      if (isEmbedded && onSalvar) {
        onSalvar(values);
        return;
      }
      
      // Otherwise create a solar client object
      const clienteData = {
        tipo: 'solar' as const,
        geradorId: "", // Solar clients don't have a generator ID
        nome: values.nome,
        documento: values.documento,
        email: values.email || "",
        celular: values.celular || "",
        status: values.status,
        cep: values.cep || "",
        logradouro: values.logradouro || "",
        bairro: values.bairro || "",
        cidade: values.cidade || "",
        estado: values.estado || "",
        concessionaria: "", // No concessionaria for solar clients
        numeroCliente: "",
        numeroInstalacao: "",
        gastoEnergia: 0,
        tarifaEnergia: 0,
        tipoInstalacao: values.tipoInstalacao,
        potenciaInstalada: values.potenciaInstalada || 0,
        areaOcupada: values.areaOcupada || 0,
        fabricantePaineis: values.fabricantePaineis || "",
        modeloPaineis: values.modeloPaineis || "",
        quantidadePaineis: values.quantidadePaineis || 0,
        fabricanteInversor: values.fabricanteInversor || "",
        modeloInversor: values.modeloInversor || "",
        quantidadeInversores: values.quantidadeInversores || 0,
        dataInstalacao: values.dataInstalacao || "",
        criadoPor: user?.id
      };
      
      const cliente = ClienteService.create(clienteData);
      
      if (cliente) {
        toast.success("Cliente solar cadastrado com sucesso!");
        navigate(`/clientes/${cliente.id}`);
      } else {
        toast.error("Erro ao cadastrar cliente solar.");
      }
    } catch (error) {
      console.error("Erro ao cadastrar cliente solar:", error);
      toast.error("Erro ao cadastrar cliente solar");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-4">Dados Pessoais</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Cliente</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome completo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="documento"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF/CNPJ</FormLabel>
                  <FormControl>
                    <Input placeholder="CPF ou CNPJ" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="email@exemplo.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="celular"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Celular</FormLabel>
                  <FormControl>
                    <Input placeholder="(11) 98765-4321" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Ativo">Ativo</SelectItem>
                      <SelectItem value="Inativo">Inativo</SelectItem>
                      <SelectItem value="Pendente">Pendente</SelectItem>
                      <SelectItem value="Interessado">Interessado</SelectItem>
                      <SelectItem value="Instalação Concluída">Instalação Concluída</SelectItem>
                      <SelectItem value="Em Instalação">Em Instalação</SelectItem>
                      <SelectItem value="Orçamento Aprovado">Orçamento Aprovado</SelectItem>
                      <SelectItem value="Orçamento Enviado">Orçamento Enviado</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <Separator />
        
        <div>
          <h3 className="text-lg font-medium mb-4">Endereço da Instalação</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="cep"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CEP</FormLabel>
                  <FormControl>
                    <div className="flex space-x-2">
                      <CepInput value={field.value} onChange={field.onChange} onAddressFound={handleCepFound} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="logradouro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logradouro</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Rua, Avenida, etc" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bairro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bairro</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Bairro" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cidade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cidade</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Cidade" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="estado"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estado</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="UF" maxLength={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <Separator />
        
        <div>
          <h3 className="text-lg font-medium mb-4">Dados da Instalação Solar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="tipoInstalacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Instalação</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="residencial">Residencial</SelectItem>
                      <SelectItem value="comercial">Comercial</SelectItem>
                      <SelectItem value="industrial">Industrial</SelectItem>
                      <SelectItem value="rural">Rural</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="potenciaInstalada"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Potência Instalada (kWp)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      step="0.01" 
                      min="0"
                      placeholder="0.00" 
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="areaOcupada"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Área Ocupada (m²)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      step="0.01" 
                      min="0"
                      placeholder="0.00" 
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="dataInstalacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data da Instalação</FormLabel>
                  <FormControl>
                    <Input 
                      type="date"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {paineisCadastrados.length > 0 && (
              <FormItem>
                <FormLabel>Painel Cadastrado</FormLabel>
                <Select onValueChange={onPainelSelect}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um painel" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {paineisCadastrados.map(painel => (
                      <SelectItem key={painel.id} value={painel.id}>
                        {painel.marca} - {painel.modelo} ({painel.potencia}kW)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
            
            <FormField
              control={form.control}
              name="fabricantePaineis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fabricante dos Painéis</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Canadian Solar" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="modeloPaineis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Modelo dos Painéis</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: CS3W-455MS" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="quantidadePaineis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantidade de Painéis</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {inversoresCadastrados.length > 0 && (
              <FormItem>
                <FormLabel>Inversor Cadastrado</FormLabel>
                <Select onValueChange={onInversorSelect}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um inversor" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {inversoresCadastrados.map(inversor => (
                      <SelectItem key={inversor.id} value={inversor.id}>
                        {inversor.marca} - {inversor.modelo} ({inversor.potencia}kW)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
            
            <FormField
              control={form.control}
              name="fabricanteInversor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fabricante do Inversor</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Growatt" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="modeloInversor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Modelo do Inversor</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: MAC 15KTL3-X" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="quantidadeInversores"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantidade de Inversores</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 pt-4">
          {!isEmbedded && (
            <Button type="button" variant="outline" onClick={() => navigate('/clientes')}>
              Cancelar
            </Button>
          )}
          <Button 
            type="submit" 
            className="bg-amber-500 text-white hover:bg-amber-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Salvando..." : isEmbedded ? "Usar Dados" : "Salvar Cliente Solar"}
          </Button>
        </div>
      </form>
    </Form>
  );

  if (isEmbedded) {
    return formContent;
  }

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="text-energy-blue-dark">Cadastro de Cliente Solar</CardTitle>
        <CardDescription>
          Preencha os dados do cliente de instalação solar fotovoltaica.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {formContent}
      </CardContent>
    </Card>
  );
};

export default ClienteSolarForm;
